import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Youtube, Instagram, Twitter, Music2, Facebook, Plus, Unlink, Loader2, Check, Link2, AlertTriangle, Infinity as InfinityIcon, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', available: true, note: 'Upload videos + Shorts' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E1306C', available: false, note: 'Bientot disponible (Meta Graph API)' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', available: false, note: 'Bientot disponible (Meta Graph API)' },
  { id: 'x', name: 'X (Twitter)', icon: Twitter, color: '#FFFFFF', available: false, note: 'Bientot disponible (X API v2)' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, color: '#25F4EE', available: false, note: 'Bientot disponible' },
];

export default function ConnectionsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ accounts: [], counts: {}, max_per_platform: 3, unlimited: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [showYtPrewarn, setShowYtPrewarn] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/social-accounts');
      setData(res.data);
    } catch (err) {
      toast.error('Erreur de chargement des comptes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle OAuth callback query params
  useEffect(() => {
    const ytStatus = searchParams.get('youtube');
    const channel = searchParams.get('channel');
    const msg = searchParams.get('msg');
    if (ytStatus === 'success' && channel) {
      toast.success(`YouTube connecte : ${channel}`);
      load();
      setSearchParams({});
    } else if (ytStatus === 'reconnected' && channel) {
      toast.success(`YouTube reconnecte : ${channel}`);
      load();
      setSearchParams({});
    } else if (ytStatus === 'limit') {
      toast.error('Limite atteinte: 3 comptes YouTube max');
      setSearchParams({});
    } else if (ytStatus === 'error') {
      const decoded = msg ? decodeURIComponent(msg) : '';
      toast.error(`Erreur YouTube${decoded ? ': ' + decoded : ''}`, { duration: 12000 });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, load]);

  const handleConnect = async (platform) => {
    if (platform !== 'youtube') {
      toast.info('Plateforme bientot disponible');
      return;
    }
    // Show pre-warning modal before redirecting to Google
    setShowYtPrewarn(true);
  };

  const proceedYoutubeOAuth = async () => {
    setShowYtPrewarn(false);
    setConnecting('youtube');
    try {
      const res = await api.get('/youtube/connect');
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error('Impossible de recuperer l\'URL OAuth');
        setConnecting(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de connexion');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (account) => {
    if (!window.confirm(`Deconnecter ${account.account_name} ?`)) return;
    try {
      await api.delete(`/social-accounts/${account.id}`);
      toast.success(`${account.account_name} deconnecte`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3300]" />
      </div>
    );
  }

  const maxLabel = data.unlimited ? '∞' : data.max_per_platform;
  const accountsByPlatform = (platformId) =>
    (data.accounts || []).filter((a) => a.platform === platformId);

  return (
    <div className="max-w-5xl mx-auto" data-testid="connections-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight mb-2">
          Comptes connectes
        </h1>
        <p className="text-sm text-white/50">
          Connectez jusqu'a <span className="text-[#FF3300] font-medium">{maxLabel}</span> compte(s) par plateforme.
          {data.unlimited && <span className="ml-2 text-[#FFD700]"><InfinityIcon className="inline w-4 h-4" /> Illimite (VIP)</span>}
        </p>
      </div>

      <div className="space-y-6">
        {PLATFORMS.map((platform) => {
          const accounts = accountsByPlatform(platform.id);
          const count = data.counts?.[platform.id] || 0;
          const canAdd = data.unlimited || count < data.max_per_platform;
          const Icon = platform.icon;
          return (
            <div
              key={platform.id}
              className="card-surface rounded-lg p-6"
              data-testid={`platform-${platform.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: `${platform.color}15`, border: `1px solid ${platform.color}30` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: platform.color }} />
                  </div>
                  <div>
                    <h2 className="font-heading text-base font-medium text-white">{platform.name}</h2>
                    <p className="text-xs text-white/40">
                      {count}/{maxLabel} compte(s) connecte(s) {!platform.available && '· Bientot'}
                    </p>
                  </div>
                </div>
                {platform.available ? (
                  <button
                    onClick={() => handleConnect(platform.id)}
                    disabled={!canAdd || connecting === platform.id}
                    data-testid={`connect-${platform.id}-btn`}
                    className="btn-accent text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {connecting === platform.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {canAdd ? 'Connecter' : 'Limite atteinte'}
                  </button>
                ) : (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/40">
                    Bientot
                  </span>
                )}
              </div>

              {accounts.length > 0 ? (
                <div className="space-y-2">
                  {accounts.map((acc) => (
                    <div
                      key={acc.id}
                      className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg border border-white/5"
                      data-testid={`account-${acc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {acc.picture ? (
                          <img src={acc.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-white/40" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-white">{acc.account_name}</p>
                          <p className="text-xs text-white/30 flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-[#34C759]" />
                            Connecte · {new Date(acc.connected_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDisconnect(acc)}
                        data-testid={`disconnect-${acc.id}-btn`}
                        className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all"
                        title="Deconnecter"
                      >
                        <Unlink className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-white/[0.02] rounded-lg border border-dashed border-white/10">
                  <Link2 className="w-4 h-4 text-white/30" />
                  <p className="text-xs text-white/40">{platform.note}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 p-4 rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/20 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-white/60 space-y-1">
          <p className="text-white font-medium">Confidentialite et securite</p>
          <p>Vos tokens sont chiffres AES-256. ViralSynth ne publie qu'en votre nom, avec votre accord. Vous pouvez deconnecter un compte a tout moment.</p>
        </div>
      </div>

      {/* === Pre-warning modal: explain Google "unverified app" screen BEFORE redirecting === */}
      {showYtPrewarn && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowYtPrewarn(false)}
          data-testid="yt-prewarn-modal"
        >
          <div
            className="card-surface rounded-xl p-6 max-w-lg w-full border border-white/10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowYtPrewarn(false)}
              className="absolute top-3 right-3 text-white/40 hover:text-white p-1"
              aria-label="Fermer"
              data-testid="yt-prewarn-close-btn"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-5 h-5 text-[#FFD700]" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium text-white mb-1">
                  Avant de continuer
                </h3>
                <p className="text-xs text-white/50">
                  Google va afficher un ecran d'avertissement (normal pour toute app non encore verifiee).
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-5 text-sm text-white/70">
              <p>Quand vous verrez l'ecran <span className="text-white font-medium">"Google n'a pas valide cette application"</span>, suivez ces etapes :</p>

              <div className="bg-white/[0.03] rounded-lg p-3 border border-white/5 space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#FF3300]/20 text-[#FF3300] flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
                  <p>Cliquez sur <span className="text-white font-medium">"Parametres avances"</span> (en bas a gauche)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#FF3300]/20 text-[#FF3300] flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
                  <p>Cliquez sur <span className="text-white font-medium">"Acceder a viralsynth.com (non securise)"</span></p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#FF3300]/20 text-[#FF3300] flex items-center justify-center text-[10px] font-bold flex-shrink-0">3</span>
                  <p>Cliquez <span className="text-white font-medium">"Continuer"</span> puis <span className="text-white font-medium">"Autoriser"</span> pour chaque permission</p>
                </div>
              </div>

              <p className="text-xs text-white/40 italic">
                Le terme "non securise" est trompeur : ViralSynth est 100% securise (TLS, tokens chiffres AES-256).
                Cette mention apparait pour toutes les apps non encore soumises a la verification payante de Google.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowYtPrewarn(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-sm text-white/70 hover:bg-white/5 transition"
                data-testid="yt-prewarn-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={proceedYoutubeOAuth}
                className="flex-1 btn-accent text-sm py-2.5 flex items-center justify-center gap-2"
                data-testid="yt-prewarn-continue-btn"
              >
                <Youtube className="w-4 h-4" />
                Continuer vers Google
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
